import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppBar, Tabs, Tab, Icon, Menu, MenuItem } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import TabContainer from 'global/components/TabContainer';
import ReportAssessmentInfo from '../AssessmentInfo';
import ReportAssessmentScoreContent from '../AssessmentScore';
import Highlights from '../Highlights';
import ReportAssessmentPhotos from '../Assessment/ReportAssessmentPhotos';
import { get, size } from 'global/lib/lodash';
import { useScreen } from 'global/styles';
import useStyles from './ReportTabs.styles';
import { PrintView } from 'global/components/print';
import SummaryScores from '../SummaryScores';
import MultiAssessment from '../AssessmentScore/MultiAssessment';
import TasksPrintSection from '../TasksPrintSection';
import { _size } from 'global/utils';
import StatusBar from '../StatusBar';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';

const tabs = [
  {
    value: 'info',
    label: 'Info',
    Component: ReportAssessmentInfo,
  },
  {
    value: 'scores',
    label: 'Scores',
    Component: ReportAssessmentScoreContent,
    className: 'noPad',
    canShow: (props) => {
      const { assessment } = props;
      const status = get(assessment, 'data.status');
      const scores = get(assessment, 'data.scores') || {};
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      const nascores = Object.keys(scores).filter((k) => {
        const { calculatedScore } = scores[k];
        return !calculatedScore;
      });

      let hasScores = _size(nascores) < _size(scores);

      if (!hasScores && (status === 'review' || status === 'published')) {
        return false;
      }

      return true;
    },
  },
  {
    value: 'highlights',
    label: 'Highlights',
    Component: Highlights,
    canShow: (props) => {
      const { assessment, highlights, isTemplate, printView } = props;
      const hasHighlights = size(highlights.data);
      const status = get(assessment, 'data.status');
      const isLot = get(assessment, 'data.realm.tier') === 'lot';
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      if (isTemplate) {
        return false;
      }

      if (isLot) {
        return false;
      } else if (!hasHighlights && (status === 'review' || status === 'published' || printView)) {
        return false;
      }

      return true;
    },
  },
  {
    value: 'photos',
    label: 'Photos',
    Component: ReportAssessmentPhotos,
    canShow: (props) => {
      const { assessment, isTemplate } = props;
      const status = get(assessment, 'data.status');
      const photosCount = get(assessment, 'data.realmPhotoCount', 0);
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      let hasPhotos = photosCount > 0;

      if (isTemplate) {
        return false;
      }

      if (!hasPhotos && (status === 'review' || status === 'published')) {
        return false;
      }

      return true;
    },
  },
];

// Map section names to their print tab definitions
const SECTION_MAP = {
  info: { ...tabs[0], sectionLabel: 'Info' },
  score_summary: {
    Component: SummaryScores,
    pagebreak: false,
    sectionLabel: 'Score Summary',
    canShow: () => true,
  },
  highlights: { ...tabs[2], sectionLabel: 'Highlights' },
  scores: { Component: MultiAssessment, sectionLabel: 'Scores' },
  tasks: {
    Component: TasksPrintSection,
    sectionLabel: 'Tasks',
    canShow: (props) => {
      const { tasks, isTemplate, printView } = props;
      if (!printView) return false;
      if (isTemplate) return false;
      if (get(tasks, 'loading')) return false;
      return size(get(tasks, 'data')) > 0;
    },
  },
};

const DEFAULT_PRINT_OPTIONS = [
  {
    name: 'Print Report',
    tier: [],
    sections: [
      { name: 'info', order: 1, enabled: true },
      { name: 'score_summary', order: 2, enabled: true },
      { name: 'highlights', order: 3, enabled: true },
      { name: 'scores', order: 4, enabled: true },
      { name: 'tasks', order: 5, enabled: false },
    ],
  },
];

const getApplicablePrintOptions = (assessmentTier) => {
  const options = getAccountSetting(SETTINGS.assessmentPrintOptions) || DEFAULT_PRINT_OPTIONS;
  if (!Array.isArray(options) || !size(options)) return DEFAULT_PRINT_OPTIONS;

  const filtered = options.filter((opt) => {
    const tiers = get(opt, 'tier', []);
    if (!size(tiers)) return true; // empty tier = applies to all
    return tiers.includes(assessmentTier);
  });

  return size(filtered) ? filtered : DEFAULT_PRINT_OPTIONS;
};

const buildPrintTabs = (sections, allProps) => {
  const enabledSections = [...sections]
    .filter((s) => s.enabled !== false)
    .sort((a, b) => a.order - b.order);

  return enabledSections
    .map((section) => {
      const tabDef = SECTION_MAP[section.name];
      if (!tabDef) return null;
      return tabDef;
    })
    .filter((t) => t && (t.canShow ? t.canShow({ printView: true, ...allProps }) : true));
};

export default function ReportTabs(props) {
  const classes = useStyles();
  const { page, currentTabValue, assessment, canShowScores } = props;
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activePrintOption, setActivePrintOption] = useState(null);
  const [sectionToggles, setSectionToggles] = useState(null);
  const [openTrigger, setOpenTrigger] = useState(0);

  const loading = get(assessment, 'loading', false);
  const assessmentTier = get(assessment, 'data.realm.tier', '');
  const accountSettings = useSelector((state) => get(state, 'auth.accountSettings'));

  const printOptions = useMemo(() => getApplicablePrintOptions(assessmentTier), [assessmentTier, accountSettings]);
  const hasMultipleOptions = size(printOptions) > 1;

  // Determine which print option is active (default to first)
  const currentOption = activePrintOption || get(printOptions, '0');
  const currentSections = get(currentOption, 'sections', []);

  // Apply user toggles on top of the default sections
  const effectiveSections = useMemo(() => {
    if (!sectionToggles) return currentSections;
    return currentSections.map((s) => ({
      ...s,
      enabled: sectionToggles[s.name] !== undefined ? sectionToggles[s.name] : s.enabled !== false,
    }));
  }, [currentSections, sectionToggles]);

  const currentPrintTabs = useMemo(() => buildPrintTabs(effectiveSections, props), [effectiveSections, props]);

  const onSelectPrintOption = (opt) => {
    setActivePrintOption(opt);
    setSectionToggles(null);
    setMenuAnchor(null);
    setOpenTrigger((t) => t + 1);
  };

  const handlePrintClick = (e) => {
    if (hasMultipleOptions) {
      setMenuAnchor(e.currentTarget);
    }
  };

  const printBtnText = (
    <>
      <PrintIcon className={classes.onlyMobile} />
      <div className={classes.hideMobile}>Print{hasMultipleOptions ? ' \u25BE' : ''}</div>
    </>
  );

  // Build section toggle config for PrintView
  const sectionToggleConfig = currentSections.map((s) => {
    const tabDef = SECTION_MAP[s.name];
    if (!tabDef) return null;
    const hasContent = tabDef.canShow ? tabDef.canShow({ printView: true, ...props }) : true;
    if (!hasContent) return null;
    const isEnabled = sectionToggles ? (sectionToggles[s.name] !== undefined ? sectionToggles[s.name] : s.enabled !== false) : s.enabled !== false;
    return { name: s.name, label: get(tabDef, 'sectionLabel', s.name), enabled: isEnabled };
  }).filter(Boolean);

  const onToggleSection = (sectionName, enabled) => {
    setSectionToggles((prev) => ({ ...(prev || {}), [sectionName]: enabled }));
  };

  return (
    <div className={classes.root}>
      <AppBar position="static">
        <Tabs value={loading ? 'info' : currentTabValue} className={classes.tab} variant="scrollable" aria-label="scrollable auto tabs">
          {tabs.map((item, index) => {
            const canShow = item.canShow ? item.canShow(props) : true;
            if (!canShow) {
              return null;
            }
            return <Tab component={Link} to={`${page}/${item.value}`} label={item.label} value={item.value} key={item.value} aria-label={item.value} />;
          })}
        </Tabs>
        <PrintView
          items={currentPrintTabs.map((tab, i) => {
            return {
              title: i === 0 ? get(props, `assessment.data.name`, '') : undefined,
              titleStyle: { fontSize: 30, paddingTop: '16px', fontWeight: 'bold' },
              render: (rprops) => <tab.Component {...props} {...(rprops || {})} printView={true} />,
            };
          })}
          assessment={props.assessment.data}
          useLoaded={true}
          hideCoverPage={true}
          buttonContainerStyle={{}}
          onOpen={() => get(props, 'scores.update', () => 0)()}
          printBtn={{
            text: printBtnText,
            onClick: hasMultipleOptions ? handlePrintClick : undefined,
          }}
          exportBtn={{ disabled: !canShowScores, text: <><Icon className={classes.onlyMobile}>insert_chart</Icon><div className={classes.hideMobile}>Download Data</div></> }}
          sectionToggles={sectionToggleConfig}
          onToggleSection={onToggleSection}
          openTrigger={openTrigger}
        />
        {hasMultipleOptions && (
          <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
            {printOptions.map((opt, i) => (
              <MenuItem key={i} onClick={() => onSelectPrintOption(opt)} selected={opt === currentOption}>
                {opt.name}
              </MenuItem>
            ))}
          </Menu>
        )}
      </AppBar>
      {tabs.map((item) => {
        const TabComponent = item.Component;
        const className = get(classes, get(item, 'className'));
        return (
          currentTabValue === item.value && (
            <TabContainer className={className} key={item.value}>
              <StatusBar {...props} />
              <TabComponent {...props} className={classes.content} />
            </TabContainer>
          )
        );
      })}
    </div>
  );
}
